// FaceMaker 共通型定義。
// .fmavatar (SPEC_FMAVATAR.md) の読み込み結果と、
// アプリ内部で扱う編集状態（クリップ / ジェスチャー割り当て）を定義する。

/** SPEC_FMAVATAR §3.4 上限値 */
export const LIMITS = {
  meshCount: 64,
  vertexCount: 2_097_152,
  totalIndexCount: 6_291_456,
  blendshapeCount: 1024,
  submeshCount: 32,
  totalUncompressedBytes: 1024 * 1024 * 1024,
  /**
   * WebGL2 の MAX_ARRAY_TEXTURE_LAYERS 保証値。
   * Web プレビューは Three.js 標準の morph 機構（array texture の layer 方向に
   * シェイプキーを積む方式）を使わず、2D デルタテクスチャの height 方向に積む
   * 自前実装に置き換えたため、この値はもう描画上の制限ではない。
   * 実質上限は MAX_TEXTURE_SIZE（通常 8192 以上）で、超過時は viewer 側で検出する。
   * 参考値として残す。
   */
  guaranteedMorphTargets: 256,
  textureMinPx: 1,
  textureMaxPx: 2048,
} as const;

/** 本実装が対応する .fmavatar manifest formatVersion */
export const FMAVATAR_FORMAT_VERSION = 1;
/** meshes.bin 内部の formatVersion */
export const MESHBIN_FORMAT_VERSION = 1;
/** 本実装が書き出す .fmapply formatVersion */
export const FMAPPLY_FORMAT_VERSION = 1;

export const TOOL_VERSION = "0.1.0";

// ---------------------------------------------------------------------------
// manifest.json
// ---------------------------------------------------------------------------

export interface FmAvatarStats {
  meshCount?: number;
  totalVertexCount?: number;
  totalBlendshapeCount?: number;
  materialCount?: number;
  textureCount?: number;
}

export interface FmAvatarManifest {
  formatVersion: number;
  toolVersion?: string;
  avatarName: string;
  avatarUid?: string;
  createdAt: string;
  unityVersion?: string;
  stats?: FmAvatarStats;
}

// ---------------------------------------------------------------------------
// meshes.bin
// ---------------------------------------------------------------------------

export interface MeshBinSubMesh {
  indexOffset: number;
  indexCount: number;
}

/** スパース形式のブレンドシェイプデルタ */
export interface MeshBinBlendshape {
  /** 昇順・重複なしの頂点インデックス */
  indices: Uint32Array;
  /** indices.length * 3 */
  posDeltas: Float32Array;
  /** indices.length * 3 */
  normDeltas: Float32Array;
}

export interface MeshTransform {
  position: [number, number, number];
  /** Quaternion x,y,z,w */
  rotation: [number, number, number, number];
}

export interface ParsedMeshBlock {
  meshIndex: number;
  vertexCount: number;
  submeshCount: number;
  blendshapeCount: number;
  totalIndexCount: number;
  indexFormat: 0 | 1;
  transform: MeshTransform;
  positions: Float32Array;
  normals: Float32Array;
  tangents: Float32Array;
  uv0: Float32Array;
  submeshes: MeshBinSubMesh[];
  indices: Uint16Array | Uint32Array;
  blendshapes: MeshBinBlendshape[];
}

export interface ParsedMeshesBin {
  formatVersion: number;
  meshCount: number;
  blocks: ParsedMeshBlock[];
}

// ---------------------------------------------------------------------------
// materials.json
// ---------------------------------------------------------------------------

export type ColorSpace = "sRGB" | "Linear";
export type RenderingMode = "Opaque" | "Cutout" | "Transparent";
export type AlphaMode = "Opaque" | "AlphaClip" | "AlphaBlend";
export type CullMode = "Off" | "Front" | "Back";

export interface TextureRef {
  file: string;
  width: number;
  height: number;
  colorSpace: ColorSpace;
  uvScale?: [number, number];
  uvOffset?: [number, number];
}

export interface MaterialProperties {
  mainColor: [number, number, number, number];
  shadeColor: [number, number, number, number];
  shadowBorder: number;
  shadowBlur: number;
  outlineColor: [number, number, number, number];
  outlineWidth: number;
  alphaCutoff: number;
  /** Unity側 `_ZWrite` の実値。AlphaBlend系マテリアルでも ZWrite=On なら true になる。 */
  zWrite: boolean;
  /** Unity側 `_ColorMask` の実値（0でfalse）。falseは「深度のみ書き込み、色は一切描画しない」マスク専用パス。 */
  colorWrite: boolean;
  cull: CullMode;
}

export interface FmMaterial {
  index: number;
  name: string;
  shaderType: string;
  renderingMode: RenderingMode;
  alphaMode: AlphaMode;
  textures: {
    mainTex: TextureRef;
    shadeTex?: TextureRef;
    outlineMask?: TextureRef;
    normalMap?: TextureRef;
  };
  properties: MaterialProperties;
}

export interface FmMaterialsFile {
  formatVersion: number;
  materials: FmMaterial[];
}

// ---------------------------------------------------------------------------
// face.json
// ---------------------------------------------------------------------------

export interface FaceMeshInfo {
  index: number;
  name: string;
  hierarchyPath: string;
  submeshMaterials: number[];
  blendshapeNames: string[];
}

export const VISEME_KEYS = [
  "sil", "aa", "ch", "dd", "e", "ff", "ih", "kk",
  "nn", "oh", "ou", "pp", "rr", "ss", "th",
] as const;
export type VisemeKey = (typeof VISEME_KEYS)[number];

export interface SpecialShapes {
  visemes: Record<VisemeKey, string | null>;
  blink: { left: string | null; right: string | null; combined?: string | null };
}

export interface ExistingGestureEntry {
  gesture: number;
  clipRef: number;
}
export interface ExistingComboEntry {
  left: number;
  right: number;
  clipRef: number;
}
export interface ExistingGestures {
  left: ExistingGestureEntry[];
  right: ExistingGestureEntry[];
  combos: ExistingComboEntry[];
}

export interface BlendshapeValue {
  meshIndex: number;
  name: string;
  value: number;
}

export interface ExistingClip {
  index: number;
  name: string;
  type: string;
  assetGuid?: string;
  blendshapes: BlendshapeValue[];
}

export interface MenuCandidate {
  label: string;
  clipRef: number | null;
}

export interface FmFaceFile {
  formatVersion: number;
  meshes: FaceMeshInfo[];
  specialShapes: SpecialShapes;
  existingGestures: ExistingGestures;
  existingClips: ExistingClip[];
  menuCandidates: MenuCandidate[];
}

// ---------------------------------------------------------------------------
// 読み込み結果
// ---------------------------------------------------------------------------

export interface LoadedAvatar {
  /** IndexedDB のキー。常にインポートごとに生成する一意なID */
  id: string;
  manifest: FmAvatarManifest;
  face: FmFaceFile;
  materials: FmMaterialsFile;
  meshes: ParsedMeshesBin;
  /** zip 内パス → PNG バイト列 */
  textures: Map<string, Uint8Array>;
  /** 読み込み時に出た非致命的な警告 */
  warnings: string[];
  loadedAt: string;
}

/**
 * 「メッシュ index + シェイプ名」を一意に指す文字列キー。
 * 区切り文字はシェイプキー名に絶対に現れない U+001F（Unit Separator）を使う。
 * 空白や "/" は Unity のシェイプキー名に含まれうるため使えない。
 */
export type ShapeKey = string;

const SHAPE_KEY_SEP = String.fromCharCode(0x1f);

export function shapeKeyOf(meshIndex: number, name: string): ShapeKey {
  return `${meshIndex}${SHAPE_KEY_SEP}${name}`;
}

export function parseShapeKey(key: ShapeKey): { meshIndex: number; name: string } {
  const sep = key.indexOf(SHAPE_KEY_SEP);
  if (sep < 0) {
    throw new Error(`不正な ShapeKey です: ${JSON.stringify(key)}`);
  }
  return { meshIndex: Number(key.slice(0, sep)), name: key.slice(sep + 1) };
}

// ---------------------------------------------------------------------------
// 編集状態（クリップ / 割り当て）
// ---------------------------------------------------------------------------

/**
 * Tracking Control 設定（SPEC_FMAPPLY §4.4.1）。
 * .fmapply では割り当てエントリごとに出力するが、UI 上は「クリップの性質」として
 * 1 クリップ 1 設定で持ち、書き出し時に各割り当てへ複製する。
 */
export interface TrackingSetting {
  /** animation = 自動まばたきを止める / tracking = まばたきを残す */
  eyes: "animation" | "tracking";
  /** expression = 表情の口を優先 / lipsync = リップシンクに譲る */
  mouth: "expression" | "lipsync";
}

/** SPEC_FMAPPLY §4.4.1 の既定値 */
export const DEFAULT_TRACKING: TrackingSetting = { eyes: "animation", mouth: "expression" };

export function trackingOf(clip: { tracking?: TrackingSetting } | null | undefined): TrackingSetting {
  return { ...DEFAULT_TRACKING, ...(clip?.tracking ?? {}) };
}

export function isDefaultTracking(t: TrackingSetting | undefined): boolean {
  return t === undefined || (t.eyes === DEFAULT_TRACKING.eyes && t.mouth === DEFAULT_TRACKING.mouth);
}

/** キーフレーム 1 枚（SPEC_FMAPPLY §2.3）。time は秒。 */
export interface ClipKeyframe {
  time: number;
  shapes: BlendshapeValue[];
}

export interface FaceClip {
  index: number;
  name: string;
  /** "static" = 単一ポーズ / "keyframe" = 時間軸アニメーション（M5） */
  type: "static" | "keyframe";
  durationSeconds: number;
  assetGuid: string | null;
  /**
   * 静的クリップの値。keyframe クリップでは「代表ポーズ」として
   * プレビュー/一覧表示にのみ使い、書き出しには含めない。
   */
  shapes: BlendshapeValue[];
  /** type === "keyframe" のときの時間軸（time 昇順・2 枚以上） */
  keyframes?: ClipKeyframe[];
  /** keyframe クリップをループ再生するか（既定 false） */
  loop?: boolean;
  /** 省略時は DEFAULT_TRACKING */
  tracking?: TrackingSetting;
}

/** メニュー/トグル表情 1 件（SPEC_FMAPPLY §4.6） */
export interface MenuEntry {
  /** 1 以上・重複不可。0 は「表情なし」として予約 */
  index: number;
  label: string;
  /** clips[].index。null は「表情なし」の空ステート */
  clipIndex: number | null;
  iconGuid?: string | null;
}

/** .fmapply assign.json の options（SPEC_FMAPPLY §4.3） */
export interface ApplyOptions {
  parameterPrefix: string;
  priorityHand: "left" | "right" | "none";
  suppressOtherHandOnPriority: boolean;
  writeDefaults: "auto" | "on" | "off";
  menuInstallPath: string | null;
  menuLabel: string;
  overrideExistingGestureLayers: boolean;
  /** ジェスチャー/メニュー未割り当てのクリップも含め、全クリップを素の .anim として
   * 安定したフォルダ（Generated/<アバター名>/Clips）へ書き出すか。 */
  exportAllClipsAsAnim: boolean;
}

export const DEFAULT_APPLY_OPTIONS: ApplyOptions = {
  parameterPrefix: "FM_",
  priorityHand: "none",
  suppressOtherHandOnPriority: true,
  writeDefaults: "auto",
  menuInstallPath: null,
  menuLabel: "表情",
  overrideExistingGestureLayers: true,
  exportAllClipsAsAnim: false,
};

/** 左右コンビネーション 1 セル分（SPEC_FMAPPLY §4.4 combos[]） */
export interface ComboAssign {
  /** 0〜7 */
  left: number;
  /** 0〜7 */
  right: number;
  /** clips[].index。null は「割り当てなし」 */
  clipIndex: number | null;
}

export type Hand = "left" | "right";

/** VRChat 標準ジェスチャー 0..7 */
export const GESTURE_NAMES = [
  "Neutral", "Fist", "HandOpen", "FingerPoint", "Victory", "RockNRoll", "HandGun", "ThumbsUp",
] as const;
export const GESTURE_LABELS_JA = [
  "ニュートラル", "グー", "パー", "指差し", "ピース", "ロック", "指鉄砲", "サムズアップ",
] as const;

export interface ProjectState {
  avatarId: string;
  clips: FaceClip[];
  /** gesture(0..7) → clip index | null */
  gestures: { left: (number | null)[]; right: (number | null)[] };
  /** 左右コンビネーション。基本の左右割り当てより優先される */
  combos: ComboAssign[];
  /** メニュー/トグル表情（M5） */
  menu: MenuEntry[];
  applyMode: "ndmf" | "direct";
  /** 書き出しオプション。未設定のフィールドは DEFAULT_APPLY_OPTIONS で補う */
  options?: Partial<ApplyOptions>;
  /** 最終更新日時（ISO文字列）。ブラウザ側の作業内容を保存するたびに更新する。ファイル自体には含まれない */
  updatedAt?: string;
}

export function emptyProjectState(avatarId: string): ProjectState {
  return {
    avatarId,
    clips: [],
    gestures: {
      left: new Array<number | null>(8).fill(null),
      right: new Array<number | null>(8).fill(null),
    },
    combos: [],
    menu: [],
    applyMode: "ndmf",
    options: {},
  };
}

/**
 * IndexedDB に保存された旧バージョン（M1）のプロジェクトを現行スキーマへ補完する。
 * 破壊的変更ではなくフィールド追加のみなので、欠けている分を既定値で埋める。
 */
export function normalizeProjectState(p: ProjectState): ProjectState {
  if (!Array.isArray(p.combos)) p.combos = [];
  p.combos = p.combos.filter(
    (c) =>
      Number.isInteger(c?.left) && c.left >= 0 && c.left <= 7 &&
      Number.isInteger(c?.right) && c.right >= 0 && c.right <= 7,
  );
  if (typeof p.gestures !== "object" || p.gestures === null) {
    p.gestures = { left: [], right: [] };
  }
  for (const hand of ["left", "right"] as const) {
    const arr = Array.isArray(p.gestures[hand]) ? p.gestures[hand] : [];
    p.gestures[hand] = new Array<number | null>(8).fill(null).map((_, i) => arr[i] ?? null);
  }
  if (!Array.isArray(p.clips)) p.clips = [];
  for (const c of p.clips) {
    if (c.type !== "keyframe") c.type = "static";
    if (c.type === "keyframe" && !Array.isArray(c.keyframes)) {
      // keyframes が壊れている場合は静的クリップへ降格させる
      c.type = "static";
      delete c.keyframes;
    }
    if (c.type === "keyframe" && c.keyframes && c.keyframes.length > 0) {
      // 代表ポーズ（shapes）が最初のキーフレームと食い違っている旧データを補正する
      // （記録のたびに同期されていなかったバージョンで保存されたプロジェクト対策）。
      const sorted = c.keyframes.slice().sort((a, b) => a.time - b.time);
      c.keyframes = sorted;
      c.shapes = sorted[0].shapes.map((s) => ({ ...s }));
    }
  }
  if (!Array.isArray(p.menu)) p.menu = [];
  // index の重複・範囲外を取り除く（1 以上・255 件まで）
  {
    const seen = new Set<number>();
    p.menu = p.menu
      .filter((m) => m && Number.isInteger(m.index) && m.index >= 1 && typeof m.label === "string" && m.label.length > 0)
      .filter((m) => (seen.has(m.index) ? false : (seen.add(m.index), true)))
      .slice(0, 255);
  }
  if (typeof p.options !== "object" || p.options === null) p.options = {};
  // 欠落・不正な applyMode は Web の既定（emptyProjectState と同じ "ndmf"）で補完する
  if (p.applyMode !== "ndmf" && p.applyMode !== "direct") p.applyMode = "ndmf";
  return p;
}

/** プロジェクトの書き出しオプション（未設定分は既定値で補完）。 */
export function optionsOf(p: ProjectState | null | undefined): ApplyOptions {
  return { ...DEFAULT_APPLY_OPTIONS, ...(p?.options ?? {}) };
}
